import React from "react";
import { Button } from "@/components/ui/button";
import { Printer, LogIn, UserPlus, CalendarPlus, Home, CalendarClock, KeyRound } from "lucide-react";

const steps = [
  {
    icon: LogIn,
    th: "เข้าสู่ระบบ",
    fr: "Se connecter",
    th_body: "เปิดเว็บไซต์ ng-residence.com กดปุ่ม “ระบบจัดการ” ด้านล่าง แล้วกรอกอีเมลและรหัสผ่านของคุณ",
    fr_body: "Ouvrir ng-residence.com, cliquer « Espace gestion » en bas, puis saisir e-mail et mot de passe.",
  },
  {
    icon: UserPlus,
    th: "เพิ่มผู้เช่า",
    fr: "Ajouter un locataire",
    th_body: "เมนู “ผู้เช่า” → กดปุ่ม “+ เพิ่มผู้เช่า” → กรอกชื่อ นามสกุล และเลขบัตร/พาสปอร์ต → กดบันทึก จากนั้นอัปโหลดรูปบัตรประชาชนหรือพาสปอร์ตได้",
    fr_body: "Menu « Locataires » → « + Ajouter » → nom, prénom et n° de pièce → Enregistrer. Puis importer la photo de la pièce d'identité.",
  },
  {
    icon: CalendarPlus,
    th: "สร้างการจอง",
    fr: "Créer une réservation",
    th_body: "เมนู “การจอง” → กดปุ่ม “+ จองใหม่” → เลือกผู้เช่า, ห้อง, ประเภท (รายเดือน / รายสัปดาห์ / รายวัน) และวันเริ่ม → ค่าเช่าจะเติมให้อัตโนมัติ → กดบันทึก",
    fr_body: "Menu « Réservations » → « + Nouvelle réservation » → choisir le locataire, le local, la durée (mois/semaine/jour) et la date d'entrée. Le loyer se remplit tout seul → Enregistrer.",
  },
  {
    icon: Home,
    th: "ดูผู้เช่าปัจจุบันของห้อง",
    fr: "Voir l'occupant d'une chambre",
    th_body: "เมนู “แดชบอร์ด” → กดที่ห้องใดก็ได้ → จะเห็นชื่อผู้เช่าปัจจุบัน ระยะเวลา และค่าเช่า",
    fr_body: "Menu « Tableau de bord » → cliquer sur une chambre → l'occupant actuel s'affiche (nom, période, loyer).",
  },
  {
    icon: CalendarClock,
    th: "ดูกำหนดชำระค่าเช่า",
    fr: "Voir les échéances de loyer",
    th_body: "เมนู “กำหนดชำระ” → ดูว่าห้องไหนถึงกำหนดหรือค้างชำระ (ดูอย่างเดียว)",
    fr_body: "Menu « Échéances » → voir les loyers dus ou en retard (lecture seule).",
  },
];

export default function Guide() {
  return (
    <div className="max-w-3xl">
      <style>{`@media print {
        body * { visibility: hidden !important; }
        #guide-print, #guide-print * { visibility: visible !important; }
        #guide-print { position: absolute; left: 0; top: 0; width: 100%; padding: 0 12mm; }
        .no-print { display: none !important; }
      }`}</style>

      <div className="flex items-center justify-between mb-6 no-print">
        <div>
          <h1 className="text-3xl sm:text-4xl font-light tracking-tight text-teak">คู่มือใช้งานอย่างย่อ</h1>
          <p className="text-muted-foreground mt-1">Guide rapide — réception</p>
        </div>
        <Button onClick={() => window.print()} className="bg-teak hover:bg-teak-hover text-white" data-testid="guide-print-btn">
          <Printer className="w-4 h-4 mr-2" /> พิมพ์ / Imprimer
        </Button>
      </div>

      <div id="guide-print" className="bg-white rounded-lg border border-border p-8 space-y-6">
        <div className="text-center border-b border-border pb-4">
          <div className="text-2xl font-semibold text-teak">NG Residence</div>
          <div className="text-sm text-muted-foreground">คู่มือใช้งานอย่างย่อ · Guide rapide</div>
        </div>

        {steps.map((s, i) => (
          <div key={i} className="flex gap-4" data-testid={`guide-step-${i}`}>
            <div className="shrink-0">
              <div className="w-10 h-10 rounded-full bg-teak text-white flex items-center justify-center font-semibold">{i + 1}</div>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 text-teak">
                <s.icon className="w-4 h-4" />
                <h3 className="font-semibold">{s.th} <span className="text-muted-foreground font-normal text-sm">· {s.fr}</span></h3>
              </div>
              <p className="text-sm mt-1">{s.th_body}</p>
              <p className="text-xs text-muted-foreground mt-1 italic">{s.fr_body}</p>
            </div>
          </div>
        ))}

        <div className="rounded-md bg-sand/50 border border-border p-4 flex gap-3">
          <KeyRound className="w-5 h-5 text-teak shrink-0 mt-0.5" />
          <div>
            <p className="text-sm">เปลี่ยนรหัสผ่านได้ที่ปุ่ม “เปลี่ยนรหัสผ่าน” ด้านล่างซ้าย ถ้าลืมรหัสผ่าน แจ้งผู้ดูแลระบบให้ตั้งใหม่ให้</p>
            <p className="text-xs text-muted-foreground italic mt-1">Changer son mot de passe via « Changer mon mot de passe » (en bas à gauche). En cas d'oubli, l'administrateur le réinitialise.</p>
          </div>
        </div>

        <p className="text-center text-xs text-muted-foreground">ไม่ต้องกังวล ทุกอย่างบันทึกเมื่อกด “บันทึก” · Pas de stress : tout est enregistré quand on clique « Enregistrer ».</p>
      </div>
    </div>
  );
}
